import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Link from "@/components/feature/LocaleLink";
import logoWhite from "@/assets/images/logo-white.png";
import { isEnPath, localize, stripEn } from "@/lib/locale";

// to: 홈 섹션은 "/#section", 서브페이지는 "/path" 또는 "/path#section"
type NavLink = { labelKey: string; to: string };

const NAV_ITEMS: NavLink[] = [
  { labelKey: "nav.about", to: "/about" },
  { labelKey: "nav.maintenance", to: "/services" },
  { labelKey: "nav.security", to: "/solutions" },
  { labelKey: "nav.satellite", to: "/satellite" },
  { labelKey: "nav.performance", to: "/about#performance" },
  { labelKey: "nav.support", to: "/services#process" },
];

// 홈 섹션 id (페이지 순서). 현재 위치 표시에 사용
const SPY_SECTIONS = ["top", "intro", "business", "faq", "news", "contact"];
// 네비게이션 바 높이(80px) + 여유분
const SPY_OFFSET = 120;

export default function Navbar() {
  const { t } = useTranslation();
  const { pathname: fullPath, hash } = useLocation();
  // 메뉴 강조 · 홈 판별은 언어 접두어(/en)를 뗀 주소로
  const pathname = stripEn(fullPath);
  const isHome = pathname === "/";
  const [scrolledRaw, setScrolledRaw] = useState(false);
  // 프레임은 항상 다크(네이비). 맨 위에서는 투명, 스크롤하거나 서브페이지에서는 짙은 남색으로 채움
  const scrolled = scrolledRaw || !isHome;
  const [openMobile, setOpenMobile] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  // 페이지 스크롤 진행도(0~1): 네비게이션 아래 주황 진행선
  const [progress, setProgress] = useState(0);

  const isActive = (item: NavLink) => {
    const [path, itemHash] = item.to.split("#");
    if (path === "/" || path === "") return isHome && activeSection === itemHash;
    if (pathname !== path) return false;
    // 같은 페이지를 가리키는 메뉴가 여럿이면(회사소개 / 사업실적) 해시가 일치하는 쪽만 강조
    const siblings = NAV_ITEMS.filter((n) => n.to.split("#")[0] === path);
    if (siblings.length === 1) return true;
    return (hash || "") === (itemHash ? `#${itemHash}` : "");
  };

  const topLinkClass = (active: boolean) =>
    `inline-flex items-center gap-1 px-2.5 2xl:px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
      active ? "text-accent-300" : "text-background-50/90 hover:text-background-50"
    }`;

  const lang = isEnPath(fullPath) ? "EN" : "KR";

  // 언어 전환 = 같은 페이지의 다른 언어 주소로 이동하는 링크 (/about ↔ /en/about). 언어는 주소가 정합니다.
  // 버튼이 아닌 링크라 검색 로봇도 영문 · 국문 페이지를 서로 찾아갈 수 있습니다.
  const langHref = (l: string) => localize(`${pathname}${hash}`, l === "EN");

  useEffect(() => {
    const onScroll = () => {
      setScrolledRaw(window.scrollY > 40);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      let current = "top";
      for (const id of SPY_SECTIONS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= SPY_OFFSET) current = id;
      }
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-sm border-b ${
        scrolled ? "bg-foreground-950/90 border-background-50/30" : "bg-transparent border-background-50/20"
      }`}
    >
      <div className="scroll-progress" style={{ "--scroll-p": progress } as React.CSSProperties} aria-hidden="true"></div>
      <div className="w-full px-4 md:px-10">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
            <img src={logoWhite} alt="오션커넥트 OCEAN CONNECT — 선박 IT 통합관리" className="h-9 w-auto" />
            {/* 로고 옆 영문 문구: 메뉴와 겹치지 않도록 넓은 화면(1600px 이상)에서만 표시 (1280 · 1366px 노트북에서 겹치던 문제 수정) */}
            <span
              className={`${lang === "EN" ? "hidden min-[1800px]:block" : "hidden min-[1600px]:block"} text-[10px] tracking-[0.2em] text-background-100/80 font-label`}
            >
              VESSEL IT · INTEGRATED MANAGEMENT
            </span>
          </Link>

          {/* Desktop Nav — 영문 메뉴는 글자가 길어 1280px 미만에서는 메뉴 버튼(모바일 메뉴)으로 표시 */}
          <ul className={`${lang === "EN" ? "hidden xl:flex" : "hidden lg:flex"} items-center gap-1`}>
            {NAV_ITEMS.map((item) => (
              <li key={item.labelKey}>
                <Link
                  to={item.to}
                  onClick={() => setOpenMobile(false)}
                  aria-current={isActive(item) ? "true" : undefined}
                  className={topLinkClass(isActive(item))}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right */}
          <div className={`${lang === "EN" ? "hidden xl:flex" : "hidden lg:flex"} items-center gap-3`}>
            {/* 24/7 대표번호: 메뉴와 겹치지 않는 폭(1280px 이상)에서 상시 노출. 그보다 좁으면 모바일 메뉴 · 문의 영역에서 안내 */}
            <a
              href="tel:+82517150611"
              className="hidden xl:flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors text-background-50 hover:text-accent-300 font-label"
            >
              <i className="ri-phone-fill"></i>
              051-715-0611
            </a>
            {/* 언어 선택: 데이터플레어처럼 KOREAN | ENGLISH, 선택 언어 아래 주황 밑줄 */}
            <div className="flex items-center text-xs font-label tracking-wider">
              {["KR", "EN"].map((l, i) => (
                <RouterLink
                  key={l}
                  to={langHref(l)}
                  hrefLang={l === "KR" ? "ko" : "en"}
                  aria-current={lang === l ? "true" : undefined}
                  className={`relative px-2 py-1 font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    lang === l ? "text-background-50" : "text-background-100/60 hover:text-background-50"
                  } ${i === 1 ? "border-l border-background-50/30" : ""}`}
                >
                  {l === "KR" ? "KOREAN" : "ENGLISH"}
                  {lang === l && (
                    <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 bg-[var(--brand-orange)]"></span>
                  )}
                </RouterLink>
              ))}
            </div>
            <Link
              to="/#contact"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              {t("nav.contact")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpenMobile((v) => !v)}
            className={`${lang === "EN" ? "xl:hidden" : "lg:hidden"} w-10 h-10 flex items-center justify-center rounded-md cursor-pointer text-background-50`}
            aria-label="menu"
          >
            <i className={`text-2xl ${openMobile ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </nav>
      </div>

      {/* Mobile menu */}
      {openMobile && (
        <div className={`${lang === "EN" ? "xl:hidden" : "lg:hidden"} bg-foreground-950 border-t border-background-50/20 max-h-[80vh] overflow-y-auto`}>
          <div className="px-4 py-4 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.labelKey}
                to={item.to}
                onClick={() => setOpenMobile(false)}
                aria-current={isActive(item) ? "true" : undefined}
                className={`block py-3 font-semibold border-b border-background-50/10 cursor-pointer ${
                  isActive(item) ? "text-accent-300" : "text-background-50"
                }`}
              >
                {t(item.labelKey)}
              </Link>
            ))}
            <Link
              to="/#contact"
              onClick={() => setOpenMobile(false)}
              className="block py-3 font-semibold text-background-50 border-b border-background-50/10 cursor-pointer"
            >
              {t("nav.contact")}
            </Link>
            <a href="tel:+82517150611" className="flex items-center gap-2 py-3 font-semibold text-accent-300 cursor-pointer">
              <i className="ri-phone-fill"></i>
              051-715-0611
            </a>
            <div className="flex items-center gap-1 text-xs pt-2 font-label tracking-wider">
              {["KR", "EN"].map((l) => (
                <RouterLink
                  key={l}
                  to={langHref(l)}
                  hrefLang={l === "KR" ? "ko" : "en"}
                  onClick={() => setOpenMobile(false)}
                  aria-current={lang === l ? "true" : undefined}
                  className={`px-3 py-2 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    lang === l ? "bg-primary-600 text-background-50" : "text-background-100/70 hover:text-background-50"
                  }`}
                >
                  {l === "KR" ? "KOREAN" : "ENGLISH"}
                </RouterLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
